import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { useInitials } from "@/hooks/use-initials"
import { cn } from "@/lib/utils"
import type { MissionSocialProof } from "@/types/collab"

interface SocialProofClusterProps {
  socialProof: MissionSocialProof
  className?: string
}

/**
 * OKS-472 — Overlaid social-proof cluster for the MissionCard cover
 * (bottom-left): up to 3 stacked crewmate avatars + "{N} crewmates posted ·
 * {M} spots left" (Figma `164:695`).
 *
 * Display-only (TRD KD-1) — renders whatever the brand-internal aggregate
 * surfaced. PII floor: first name (for the avatar fallback initials + alt) +
 * avatar only; no surname/handle. The "spots left" clause is appended ONLY
 * when `spots_left` is non-null. The posted clause is shown only when ≥1
 * crewmate posted (a slot-cap-only mission shows just the spots clause).
 *
 * Pointer events are disabled so the whole-card link underneath stays the
 * single tap target (UX Spec §6). The cluster is `aria-hidden` because its
 * text is also conveyed to SR users via the card's own accessible name.
 */
export function SocialProofCluster({
  socialProof,
  className,
}: SocialProofClusterProps) {
  const { t } = useTranslation()
  const getInitials = useInitials()
  const {
    crewmates_posted: posted,
    recent_avatars: avatars,
    spots_left,
  } = socialProof

  const clauses: string[] = []
  if (posted > 0) {
    clauses.push(t("missions.card.crewmates_posted", { count: posted }))
  }
  if (spots_left !== null) {
    clauses.push(t("missions.card.spots_left", { count: spots_left }))
  }

  // Nothing to render (e.g. spots_left null AND zero posters) — defensive; the
  // backend omits `social_proof` entirely in that case.
  if (clauses.length === 0) return null

  return (
    <div
      className={cn(
        "pointer-events-none flex items-center gap-2 rounded-full bg-black/55 px-2 py-1 text-xs font-medium text-white backdrop-blur-sm",
        className,
      )}
      data-testid="mission-card-social-proof"
      aria-hidden="true"
    >
      {avatars.length > 0 && (
        <div className="flex -space-x-2" data-testid="mission-card-avatars">
          {avatars.map((a, i) => (
            <Avatar
              key={`${a.first_name}-${i}`}
              className="border-background size-5 border"
            >
              <AvatarImage src={a.avatar_url ?? undefined} alt="" />
              <AvatarFallback className="bg-muted text-muted-foreground text-[8px] font-semibold">
                {getInitials(a.first_name)}
              </AvatarFallback>
            </Avatar>
          ))}
        </div>
      )}
      <span>{clauses.join(" · ")}</span>
    </div>
  )
}
